import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiWifi, FiDroplet, FiWind, FiTv, FiCoffee, FiArrowRight } = FiIcons;

function Rooms() {
  const rooms = [
    {
      id: 1,
      name: 'Family Room',
      image: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      price: 'LKR 8,000',
      capacity: '4-5 Guests',
      description: 'Spacious family accommodation with triple beds and stunning mountain views. Perfect for groups exploring Morning Side together.',
      amenities: ['Triple Beds', 'Mountain View', 'Hot Water', 'AC', 'Free WiFi', 'Mini Fridge'],
      features: [FiUsers, FiWifi, FiDroplet, FiWind, FiTv, FiCoffee]
    },
    {
      id: 2,
      name: 'Double Room',
      image: 'https://images.unsplash.com/photo-1618773928121-c32242e63f39?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      price: 'LKR 6,500',
      capacity: '2 Guests',
      description: 'Comfortable and private accommodation for couples. Enjoy intimacy and comfort before your rainforest adventure.',
      amenities: ['Queen Bed', 'Garden View', 'Hot Water', 'AC', 'Free WiFi', 'Work Desk'],
      features: [FiUsers, FiWifi, FiDroplet, FiWind, FiTv, FiCoffee]
    }
  ];

  return (
    <section id="rooms" className="py-20 bg-gradient-to-b from-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
            Comfortable
            <span className="gradient-text block">Accommodations</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Rest well in our thoughtfully designed rooms, each equipped with modern amenities 
            and the reliability you need for your Morning Side expedition.
          </p>
        </motion.div>

        {/* Rooms Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {rooms.map((room, index) => (
            <motion.div
              key={room.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 0.8 }}
              whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
              className="glass-morphism rounded-2xl overflow-hidden hover:glass-morphism-dark transition-all duration-300"
            >
              {/* Room Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={room.image}
                  alt={room.name}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                />
                <div className="absolute top-4 left-4">
                  <span className="glass-morphism px-3 py-1 rounded-full text-sm font-medium text-white">
                    {room.capacity}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-emerald-500 px-3 py-1 rounded-full text-sm font-bold text-white">
                    {room.price}/night
                  </span>
                </div>
              </div>

              {/* Room Details */}
              <div className="p-6">
                <h3 className="text-2xl font-serif font-bold text-white mb-3">{room.name}</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">{room.description}</p>

                {/* Amenities Grid */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {room.amenities.map((amenity, idx) => (
                    <div key={amenity} className="flex items-center space-x-2 text-sm text-gray-300">
                      <SafeIcon icon={room.features[idx]} className="w-4 h-4 text-emerald-400" />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>

                {/* Book Button */}
                <Link
                  to="/booking"
                  className="liquid-button w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2 group"
                >
                  <span>Book This Room</span>
                  <SafeIcon 
                    icon={FiArrowRight} 
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" 
                  />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="glass-morphism rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-serif font-bold text-white mb-4">
              Book Direct & Save 15%
            </h3>
            <p className="text-gray-300 mb-6">
              Skip the commission fees and enjoy exclusive perks when you book directly with us. 
              Same rates as OTAs, but with added value.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-emerald-400">
              <span>✓ Free Breakfast</span>
              <span>✓ Late Checkout</span>
              <span>✓ Free Cancellation</span>
              <span>✓ Best Price Guarantee</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Rooms;